import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ImageProcessorService {

    /**
     * Ruta A: Optimiza la imagen para OCR de IA (Contraste y brillo)
     */
    async processForAI(file: File): Promise<string> {
        const img = await this.fileToImage(file);
        const canvas = document.createElement('canvas');
        const maxWidth = 1800;

        let { width, height } = this.calculateDimensions(img, maxWidth);
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) throw new Error('Error de contexto Canvas');

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        this.applyAIFilters(ctx, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        return dataUrl.split(',')[1]; // Retorna solo Base64
    }

    /**
     * Ruta B: Prepara la imagen para Storage (WebP natural)
     */
    async processForStorage(file: File): Promise<Blob> {
        const img = await this.fileToImage(file);
        const canvas = document.createElement('canvas');
        const maxWidth = 1200;

        let { width, height } = this.calculateDimensions(img, maxWidth);
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Error de contexto Canvas');

        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(img, 0, 0, width, height);

        return new Promise((resolve, reject) => {
            canvas.toBlob(b => b ? resolve(b) : reject('Error WebP'), 'image/webp', 0.80);
        });
    }

    private fileToImage(file: File): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.src = e.target?.result as string;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    private calculateDimensions(img: HTMLImageElement, max: number) {
        let { width, height } = img;
        if (width > max || height > max) {
            const ratio = Math.min(max / width, max / height);
            width *= ratio;
            height *= ratio;
        }
        return { width, height };
    }

    private applyAIFilters(ctx: CanvasRenderingContext2D, width: number, height: number) {
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        const contrast = 1.3;
        const brightness = 15;

        for (let i = 0; i < data.length; i += 4) {
            for (let j = 0; j < 3; j++) {
                let color = data[i + j];
                color = (color - 128) * contrast + 128 + brightness;
                data[i + j] = Math.min(255, Math.max(0, color));
            }
        }
        ctx.putImageData(imageData, 0, 0);
    }
}